import { MatchQuality } from './match-quality';

/**
 * Search-ranking primitives shared by the tree's `getSuggestedSortedData`
 * (in-tree relevance sort) and the app's `findSelectionListItemByLabel`
 * (selection-list fuzzy lookup). Pure scoring math — no domain logic.
 */

export type MatchType = 'direct' | 'suggested';

const POSITION_SCORES = {
  startOfString: 30,
  startOfWord: 20,
  afterPunctuation: 10,
} as const;

export const SUGGESTED_POSITION_MULTIPLIER = 0.7;
export const DIRECT_MATCH_BONUS = 1000;

const LEAF_BASE_SCORES: Record<MatchType, Record<MatchQuality, number>> = {
  direct: {
    [MatchQuality.EXACT]: 15000,
    [MatchQuality.PARTIAL]: 100,
    [MatchQuality.NONE]: 0,
  },
  suggested: {
    [MatchQuality.EXACT]: 75,
    [MatchQuality.PARTIAL]: 50,
    [MatchQuality.NONE]: 0,
  },
};

export const getBaseLeafScore = (
  matchType: MatchType,
  quality: MatchQuality
): number => LEAF_BASE_SCORES[matchType][quality];

const PARENT_BASE_SCORES: Record<MatchType, Record<MatchQuality, number>> = {
  direct: {
    [MatchQuality.EXACT]: 15,
    [MatchQuality.PARTIAL]: 10,
    [MatchQuality.NONE]: 0,
  },
  suggested: {
    [MatchQuality.EXACT]: 8,
    [MatchQuality.PARTIAL]: 5,
    [MatchQuality.NONE]: 0,
  },
};

export const getBaseParentScore = (
  matchType: MatchType,
  quality: MatchQuality
): number => PARENT_BASE_SCORES[matchType][quality];

export const getPositionBonusAnyOccurrence = (
  text: string,
  search: string
): number => {
  const normalizedText = text.toLowerCase().trim();
  const normalizedSearch = search.toLowerCase().trim();
  if (!normalizedText.includes(normalizedSearch)) return 0;

  if (normalizedText.startsWith(normalizedSearch)) {
    return POSITION_SCORES.startOfString;
  }
  if (normalizedText.includes(` ${normalizedSearch}`)) {
    return POSITION_SCORES.startOfWord;
  }

  let index = normalizedText.indexOf(normalizedSearch);
  while (index !== -1) {
    if (index > 0) {
      const beforeChar = normalizedText.charAt(index - 1) || ' ';
      if (/[^A-Za-z0-9_\s]/.test(beforeChar)) {
        return POSITION_SCORES.afterPunctuation;
      }
    }
    index = normalizedText.indexOf(normalizedSearch, index + 1);
  }

  return 0;
};

const isLenientWordMatch = (searchWord: string, valueWord: string): boolean => {
  if (!searchWord || !valueWord) return false;
  if (searchWord === valueWord) return true;
  if (valueWord.startsWith(searchWord)) return true;
  if (valueWord.endsWith(searchWord)) return true;
  if (valueWord === `${searchWord}s` || `${valueWord}s` === searchWord) {
    return true;
  }
  if (valueWord === `${searchWord}es` || `${valueWord}es` === searchWord) {
    return true;
  }
  return false;
};

export const getSuggestedMatchQuality = (
  values: string[],
  search: string
): MatchQuality => {
  if (!values?.length) return MatchQuality.NONE;

  const normalizedSearch = search.toLowerCase().trim();
  const searchWords = normalizedSearch
    .split(/\s+/)
    .filter((word) => word.length > 0);
  let hasPartialMatch = false;

  for (const value of values) {
    const normalizedValue = value.toLowerCase().trim();
    if (!normalizedValue.includes(normalizedSearch)) continue;
    hasPartialMatch = true;

    if (searchWords.length === 1) {
      const searchIndex = normalizedValue.indexOf(normalizedSearch);
      if (searchIndex !== -1) {
        const beforeChar =
          searchIndex === 0
            ? ' '
            : normalizedValue.charAt(searchIndex - 1) || ' ';
        const afterIndex = searchIndex + normalizedSearch.length;
        const afterChar =
          afterIndex >= normalizedValue.length
            ? ' '
            : normalizedValue.charAt(afterIndex) || ' ';
        const isWordBoundary = (char: string) => /[\s\W]/.test(char);
        if (isWordBoundary(beforeChar) && isWordBoundary(afterChar)) {
          return MatchQuality.EXACT;
        }
      }
    } else {
      const valueWords = normalizedValue.split(/\s+/);
      const allWordsMatch = searchWords.every((searchWord) =>
        valueWords.some(
          (valueWord) =>
            valueWord === searchWord ||
            isLenientWordMatch(searchWord, valueWord)
        )
      );
      if (allWordsMatch) return MatchQuality.EXACT;
    }
  }

  return hasPartialMatch ? MatchQuality.PARTIAL : MatchQuality.NONE;
};
